import { Price } from '@jouzen/ecom-components/v3/Price';
import type { RichTranslationValues } from 'next-intl';

import { useMembershipProduct } from '@/queries/Products';

import { LEGAL_FOOTNOTES_ID } from '../consts/ids';

export interface UseMembershipPricingProps {
  footnoteSymbol?: string;
  showCurrency?: boolean;
}

const useMembershipPricing = ({
  footnoteSymbol,
  showCurrency = true,
}: UseMembershipPricingProps = {}): RichTranslationValues => {
  const { data: membershipData } = useMembershipProduct();

  const annualPrice = membershipData?.annual?.price?.amount;
  const membershipMonthlyPrice = membershipData?.monthly?.comparePrice?.amount;
  const hasAnnualPrice = typeof annualPrice === 'number';
  const hasMonthlyPrice = typeof membershipMonthlyPrice === 'number';

  // If either membership product wasn't returned in the query response, fallback to hardcoded USD values
  const showFallbackPricing = !hasAnnualPrice || !hasMonthlyPrice;
  const currencyCode = showFallbackPricing
    ? { currencyCode: 'USD' }
    : undefined;

  return {
    annualCost: () => (
      <Price
        {...currencyCode}
        price={showFallbackPricing ? 69.99 : annualPrice}
        showCurrency={showCurrency}
      />
    ),
    footnoteLink: () => {
      return (
        <sup>
          <a href={`#${LEGAL_FOOTNOTES_ID}`}>{footnoteSymbol}</a>
        </sup>
      );
    },
    monthlyCost: () => (
      <Price
        {...currencyCode}
        price={showFallbackPricing ? 5.99 : membershipMonthlyPrice}
        showCurrency={showCurrency}
      />
    ),
  };
};

export default useMembershipPricing;
